/**
 * Gauge — label, track, fill, value; threshold-coloured (§5).
 *
 * Built now because OpsPanel genuinely renders one (it had a local `PctBar`),
 * not in anticipation of Phase B — though the box strip will reuse it.
 *
 *   <Gauge label="CPU" pct={14} value="14%" />
 *   <Gauge pct={m.use_pct} value={`${m.use_pct}%`} width="w-24" />
 *
 * Thresholds are §5's blue → warn → err. The old `PctBar` started at emerald,
 * but green means "succeeded" in §3's vocabulary and a disk at 40% has not
 * succeeded at anything — it is simply normal, which is what blue says. The
 * cut-offs themselves (75 / 90) are unchanged. The healthy fill is the lighter
 * `cyan` since the Sky blue palette (2026-10-08), as in the operator's mock.
 */

export const GAUGE_WARN_PCT = 75;
export const GAUGE_ERR_PCT = 90;

/** Threshold colour for a percentage — exported so a caller can match its
 *  numeric label to the fill without re-deriving the cut-offs. */
export function gaugeTone(pct) {
  if (pct >= GAUGE_ERR_PCT) return 'err';
  if (pct >= GAUGE_WARN_PCT) return 'warn';
  return 'blue';
}

const FILL = { blue: 'bg-cyan', warn: 'bg-warn', err: 'bg-err' };
const TEXT = { blue: 'text-t2', warn: 'text-warn', err: 'text-err' };

export function Gauge({ label, pct, value, width = '', className = '' }) {
  const safe = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;
  const tone = gaugeTone(safe);

  return (
    <div className={['flex items-center gap-3', className].join(' ')}>
      {label && (
        <span className="shrink-0 text-[11px] font-semibold tracking-[0.06em] text-t3">
          {label}
        </span>
      )}
      <div
        className={[
          'h-1.5 overflow-hidden rounded-full bg-s-4',
          width || 'flex-1 min-w-12',
        ].join(' ')}
        role="progressbar"
        aria-valuenow={safe}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label || 'usage'}
      >
        <div className={['h-full rounded-full', FILL[tone]].join(' ')} style={{ width: `${safe}%` }} />
      </div>
      {value != null && (
        <span className={['shrink-0 font-mono text-[12px]', TEXT[tone]].join(' ')}>{value}</span>
      )}
    </div>
  );
}
